import { useEffect } from 'react';


import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import File from '@/components/ui/file';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { YES_NO_BASIC_OPTIONS } from '@/constants';
import { useControlContext } from '@/contexts/ControlContext';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { computeRiskScore, deriveRiskGrading } from '@/lib/riskAssessment';

const IMPACT_LIST = [
  {
    description:
      '1. Does the proposed change affect the product or service to be out of specification?',
    key: 'riskSpecification',
    level: 1,
  },
  {
    description:
      '2. Does the proposed change affect the organisation’s compliance with the current Management System Procedures/SOPs?',
    key: 'riskManagement',
    level: 1,
  },
  {
    description:
      '3. Does the proposed change violate the required standards and/or regulations?',
    key: 'riskRegulations',
    level: 2,
  },
  {
    description:
      '4. Does the proposed change affect the organisation’s commitment to employees and consumer/user health and safety?',
    key: 'riskSafety',
    level: 2,
  },
  {
    description:
      '5. Does the proposed change affect any validated or critical process?',
    key: 'riskProcess',
    level: 1,
  },
];

const SCORE_OPTION_LIST = [
  {
    key: 'high',
    label: 'High',
    value: 2,
  },
  {
    key: 'medium',
    label: 'Medium',
    value: 1,
  },
  {
    key: 'low',
    label: 'Low',
    value: 0,
  },
];

const AssessmentTable = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();

  const handleChange = (key, target) => {
    setFormByKey(key, Number(target.value));

    // Recompute the score for this answer change and auto-fill the residual
    // (only for user edits — never on load/review). Mirrors the previous
    // watcher behavior but scoped to the edit that caused it.
    const nextScore = computeRiskScore({ ...form, [key]: Number(target.value) });
    if (nextScore !== form.riskScore) {
      setFormByKey('riskResidual', nextScore);
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Risk Impact Assessment</TableHead>
            <TableHead className="w-40">Yes / No</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {IMPACT_LIST.map(({ description, key }) => (
            <TableRow key={key}>
              <TableCell>{description}</TableCell>
              <TableCell>
                {mode === DETAIL_MODE.STATIC ? (
                  form[key] === 1 ? (
                    'Yes'
                  ) : (
                    'No'
                  )
                ) : (
                  <Select
                    disabled={readOnly}
                    onChange={({ target }) => handleChange(key, target)}
                    value={form[key]}
                  >
                    {!form[key] && <option value="">-</option>}
                    {YES_NO_BASIC_OPTIONS.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const GradingPanel = () => {
  const { form } = useControlContext();
  const { riskGrading } = form;

  return (
    <Card className="flex mb-4">
      <CardHeader className="p-4 grow">
        <div className="flex gap-4">
          {riskGrading !== 'low' && (
            <CardTitle className="text-base font-medium grow">
              Since the risk grading is under Medium to High, please justify
              or describe the action to be taken to mitigate the risk/impact.
              Attach any supporting documents, including risk assessment, as
              applicable.
            </CardTitle>
          )}
          <CardDescription className="shrink-0 w-32 ml-auto">
            <div className="text-xs mb-1">Risk Grading</div>
            <div
              className={`text-base font-semibold rounded px-2 py-1 text-white ${
                riskGrading === 'high'
                  ? 'bg-red-500'
                  : riskGrading === 'medium'
                    ? 'bg-orange-500'
                    : 'bg-green-500'
              }`}
            >
              {SCORE_OPTION_LIST.find(({ key }) => key === riskGrading)?.label}
            </div>
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const DescriptionPanel = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { riskAttachment, riskDescribe } = form;

  const handleDescriptionChange = ({ target }) =>
    setFormByKey('riskDescribe', target.value);

  const handleAttachmentChange = ({ target }) =>
    setFormByKey('riskAttachment', target.value);

  return (
    <Card className="flex mb-4">
      <CardHeader className="p-4 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium grow">
            {mode === DETAIL_MODE.STATIC ? (
              riskDescribe
            ) : (
              <Textarea
                autoResize
                className="font-mono resize-none"
                disabled={readOnly}
                onChange={handleDescriptionChange}
                placeholder="Click to add description or attachment"
                value={riskDescribe}
              />
            )}
          </CardTitle>
          <CardDescription className="w-32">
            {mode !== DETAIL_MODE.STATIC && (
              <File
                className="py-7 flex-col"
                disabled={readOnly}
                onChange={handleAttachmentChange}
                placeholder="Add Attachment"
                value={riskAttachment}
              />
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const ResidualPanel = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { riskResidual } = form;

  const residualItem = SCORE_OPTION_LIST.find(
    ({ value }) => value === Number(riskResidual)
  );

  const residualGrading = deriveRiskGrading(riskResidual);

  const colorClass =
    residualGrading === 'high'
      ? 'bg-red-500'
      : residualGrading === 'medium'
        ? 'bg-orange-500'
        : residualGrading === 'low'
          ? 'bg-green-500'
          : 'bg-gray-200';

  const handleChange = ({ target }) => {
    setFormByKey('riskResidual', target.value);
  };

  return (
    <Card className="flex mb-4">
      <CardHeader className="p-4 grow">
        <div className="flex gap-4">
          <CardTitle className="text-base font-medium grow">
            Considering the justification provided and the mitigation actions
            implemented, the residual risk level is determined to be:
          </CardTitle>
          <CardDescription className="shrink-0 w-32">
            <div className="text-xs mb-1">Residual Risk Grading</div>
            {mode === DETAIL_MODE.STATIC ? (
              <div
                className={`text-base font-semibold rounded px-2 py-1 text-white ${colorClass}`}
              >
                {residualItem?.label}
              </div>
            ) : (
              <Select
                className={`mt-2 text-base font-semibold text-white ${colorClass}`}
                disabled={readOnly}
                onChange={handleChange}
                value={riskResidual}
              >
                {!riskResidual && <option value="">-</option>}
                {SCORE_OPTION_LIST.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const ControlRiskView = () => {
  const { form, setFormByKey } = useControlContext();
  const { riskGrading } = form;

  const score = computeRiskScore(form);

  const showDetails = riskGrading !== 'low';

  useEffect(() => {
    setFormByKey('riskScore', score);
  }, [score, setFormByKey]);

  return (
    <div className="space-y-4">
      <AssessmentTable />
      <GradingPanel />
      {showDetails && <DescriptionPanel />}
      {showDetails && <ResidualPanel />}
    </div>
  );
};

export default ControlRiskView;
